import { format, isToday, isYesterday, parseISO } from "date-fns";
import type { ComponentPropsWithRef, ReactNode, RefCallback } from "react";
import { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";

import { ChatSeparator } from "./Separator";

import { t } from "i18n/i18n";
import { ChatWithLinesIcon } from "mds/icons";
import { ModelType } from "utils/type-utils";

type ChatHistoryProps<Message extends ModelType> = {
  /**
   * Array of tuples, where each tuple is a date string followed by an array of messages for that date.
   * The date string MUST be in ISO format (e.g., "2023-10-01").
   */
  messagesByDate: [string, Message[]][];
  renderMessage: (message: Message, ref: RefCallback<HTMLDivElement>) => ReactNode;
  onMessageVisible?: (entries: IntersectionObserverEntry[]) => void;
  visibilityThreshold?: number;
  separatorDateFormat?: string;
} & ComponentPropsWithRef<"div">;

export const ChatHistory = <Message extends ModelType>({
  ref,
  messagesByDate = [],
  renderMessage = () => null,
  onMessageVisible,
  visibilityThreshold = 0.5,
  separatorDateFormat = "MMM dd, yyyy",
  className,
  ...props
}: ChatHistoryProps<Message>) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const onMessageVisibleRef = useRef(onMessageVisible);
  const nodeMapRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const observesMessageVisibility = Boolean(onMessageVisible);
  onMessageVisibleRef.current = onMessageVisible;

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    if (observesMessageVisibility) {
      // `IntersectionObserver` is used to detect when chat messages are visible.
      //
      // We also use the `nodeMapRef` to keep track of the chat message nodes:
      // when a `ClassChatMessage` component mounts, it registers itself
      // with the `nodeMapRef`. When this `useEffect` re-runs, it uses the `nodeMapRef`
      // to re-register all the existing chat message nodes with the new `IntersectionObserver`.
      observer = new IntersectionObserver((entries) => onMessageVisibleRef.current?.(entries), {
        threshold: visibilityThreshold,
      });

      // Observe each existing chat message node
      for (const node of nodeMapRef.current.values()) {
        observer.observe(node);
      }

      observerRef.current = observer;
    }

    return () => {
      observer?.disconnect();
      if (observerRef.current === observer) {
        observerRef.current = null;
      }
    };
  }, [observesMessageVisibility, visibilityThreshold]);

  if (messagesByDate.length === 0) {
    return (
      <div
        className={twMerge("flex flex-col items-center justify-center gap-2", className)}
        ref={ref}
        {...props}
      >
        <ChatWithLinesIcon className="size-16 text-blue-tint-93" />
        <div className="font-normal text-black-tint-40">{t("chat.no_messages")}</div>
      </div>
    );
  }

  return (
    <div
      className={twMerge("flex flex-col-reverse gap-1.5", className)}
      ref={ref}
      role="log"
      {...props}
    >
      {messagesByDate.map(([dateString, messagesForDate]) => {
        const date = parseISO(dateString);
        const dateLabel = isToday(date)
          ? t("common.today")
          : isYesterday(date)
            ? t("common.yesterday")
            : format(date, separatorDateFormat);
        return (
          <div className="relative flex flex-col-reverse gap-1.5" key={dateString}>
            {(messagesForDate || []).map((message) =>
              renderMessage(message, (node) => {
                if (node) {
                  nodeMapRef.current.set(message.id, node);
                  observerRef.current?.observe(node);
                } else {
                  nodeMapRef.current.delete(message.id);
                }
              }),
            )}

            <ChatSeparator className="sticky top-0" key={dateString}>
              <time dateTime={dateString}>{dateLabel}</time>
            </ChatSeparator>
          </div>
        );
      })}
    </div>
  );
};
